import type { ReactNode } from 'react';

export function Card({
  children,
  className = '',
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <section
      className={`rise rounded-xl border border-edge bg-surface-raised shadow-sm ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  subtitle,
  aside,
  compact = false,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  compact?: boolean;
}) {
  return (
    <header
      className={`flex items-start justify-between gap-4 border-b border-edge ${
        compact ? 'px-3 py-2.5' : 'px-5 py-4'
      }`}
    >
      <div>
        <h2 className="text-sm font-semibold tracking-wide text-ink">{title}</h2>
        {subtitle !== undefined && <p className="mt-0.5 text-xs text-ink-faint">{subtitle}</p>}
      </div>
      {aside}
    </header>
  );
}

/**
 * A headline figure.
 *
 * `exact` is shown in the tooltip rather than the label, because the compact form
 * is what makes a row of these scannable while the full precision is what someone
 * checking a number against a spreadsheet needs. Both, one click apart.
 */
export function Kpi({
  label,
  value,
  exact,
  hint,
  tone = 'neutral',
  delay = 0,
}: {
  label: string;
  value: string;
  exact?: string;
  hint?: string;
  tone?: 'neutral' | 'profit' | 'revenue' | 'caution';
  delay?: number;
}) {
  const accent = {
    neutral: 'text-ink',
    profit: 'text-profit',
    revenue: 'text-revenue',
    caution: 'text-caution',
  }[tone];

  return (
    <Card delay={delay} className="px-5 py-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">{label}</p>
      <p className={`figure mt-2 text-2xl font-semibold ${accent}`} title={exact}>
        {value}
      </p>
      {hint !== undefined && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
    </Card>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'real' | 'warning';
  title?: string;
}) {
  const styles = {
    neutral: 'border-edge-strong text-ink-muted',
    real: 'border-profit-dim text-profit',
    warning: 'border-caution-dim text-caution',
  }[tone];

  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${styles}`}
    >
      {children}
    </span>
  );
}

export function Table({
  head,
  children,
  sortKey,
  sortDirection,
  onSort,
  compact = false,
  sticky = false,
}: {
  head: readonly {
    readonly label: string;
    readonly align?: 'left' | 'right';
    readonly sortKey?: string;
  }[];
  children: ReactNode;
  sortKey?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (key: string) => void;
  compact?: boolean;
  /** Pin the header and scroll the body inside the parent. */
  sticky?: boolean;
}) {
  const pad = compact ? 'px-2.5 py-1.5' : 'px-5 py-2.5';
  return (
    <div className={sticky ? 'min-h-0 flex-1 overflow-auto' : 'overflow-x-auto'}>
      <table className={`w-full border-collapse ${compact ? 'text-xs' : 'text-sm'}`}>
        <thead className={sticky ? 'sticky top-0 z-10' : undefined}>
          <tr className={`border-b border-edge ${sticky ? 'bg-surface-raised shadow-sm' : ''}`}>
            {head.map((column) => (
              <th
                key={column.label}
                scope="col"
                className={`${pad} text-[11px] font-medium uppercase tracking-[0.06em] text-ink-faint ${
                  column.align === 'right' ? 'text-right' : 'text-left'
                } ${column.sortKey !== undefined && onSort !== undefined ? 'cursor-pointer select-none hover:text-ink-muted' : ''}`}
                onClick={
                  column.sortKey !== undefined && onSort !== undefined
                    ? () => onSort(column.sortKey!)
                    : undefined
                }
              >
                <span className="inline-flex items-center gap-1">
                  {column.label}
                  {column.sortKey !== undefined &&
                    sortKey === column.sortKey &&
                    sortDirection !== null && (
                      <span className="text-profit">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Cell({
  children,
  align = 'left',
  muted = false,
  mono = false,
  title,
  compact = false,
}: {
  children: ReactNode;
  align?: 'left' | 'right';
  muted?: boolean;
  mono?: boolean;
  title?: string;
  compact?: boolean;
}) {
  return (
    <td
      title={title}
      className={`${compact ? 'px-2.5 py-1.5' : 'px-5 py-2.5'} ${align === 'right' ? 'text-right' : 'text-left'} ${
        muted ? 'text-ink-faint' : 'text-ink-muted'
      } ${mono ? 'figure' : ''}`}
    >
      {children}
    </td>
  );
}

export function Row({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <tr
      className={`border-b border-edge/50 transition-colors last:border-0 hover:bg-surface-overlay/60 ${onClick !== undefined ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 px-5 py-10 text-sm text-ink-faint">
      <span className="h-3 w-3 animate-pulse rounded-full bg-revenue-dim" aria-hidden />
      {label}
    </div>
  );
}

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search…',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 pl-9 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
      />
      <svg
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
  );
}

export function ExportButton({
  data,
  filename,
  headers,
}: {
  data: readonly Record<string, string | number | null | undefined>[];
  filename: string;
  headers: readonly { key: string; label: string }[];
}) {
  const handleExport = () => {
    const headerRow = headers.map((h) => h.label).join(',');
    const rows = data.map((row) =>
      headers
        .map((h) => {
          const val = row[h.key];
          if (val === null || val === undefined) return '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(','),
    );
    const csv = [headerRow, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      className="flex items-center gap-1.5 rounded-lg border border-edge px-3 py-1.5 text-xs font-medium text-ink-muted transition-colors hover:border-edge-strong hover:text-ink"
    >
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      Export CSV
    </button>
  );
}
